import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutosaveOptions<T> {
  data: T;
  onSave: (data: T) => Promise<void>;
  enabled: boolean;
  debounceMs?: number;
  validate?: (data: T) => boolean;
}

export function useAutosave<T>({
  data,
  onSave,
  enabled,
  debounceMs = 2000,
  validate,
}: UseAutosaveOptions<T>) {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const latestDataRef = useRef(data);
  const isSavingRef = useRef(false);

  latestDataRef.current = data;

  const save = useCallback(async () => {
    if (!enabled || isSavingRef.current) return;
    const currentData = latestDataRef.current;
    if (validate && !validate(currentData)) return;

    isSavingRef.current = true;
    setStatus('saving');
    try {
      await onSave(currentData);
      setStatus('saved');
      setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 3000);
    } catch {
      setStatus('error');
    } finally {
      isSavingRef.current = false;
    }
  }, [enabled, onSave, validate]);

  useEffect(() => {
    // Skip first render (initial data load / fetch)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!enabled) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(save, debounceMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [data, enabled, debounceMs, save]);

  return { status };
}
